import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CustomTabComponent } from '../../../components/ui/custom-tab/custom-tab.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AdminTestService } from '../../../core/services/admin/admin-test.service';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject as RxSubject, Subscription, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoaderComponent } from '../../../components/ui/loader/loader.component';
import { SyncfusionL10nService } from '../../utils/syncfusion-l10n.service';
import { LanguageService } from '../../../core/services/utils/language.service';
import { SwalService } from '../../../core/services/utils/swal.service';
import {
  ProfessorService,
  ProfessorStudentSessionDto,
} from '../../../core/services/professor.service';
import { StudentService } from '../../../core/services/student.service';
import { matchesSearch } from '../../../core/utils/search-text';
import { CanDirective } from '../../../core/directives/can.directive';
import { StudentSubjectProgression } from '../../../core/interfaces/student-subject-progression';

/** Un élève et ses copies, tels que l’écran les présente. */
interface StudentGroup {
  studentId: string;
  fullName: string;
  email: string;
  learningPathTitle: string;
  sessions: ProfessorStudentSessionDto[];
  /** Nombre de copies diagnostiques : sans aucune, il n’y a pas de rapport. */
  diagnosticCount: number;
  /** Dernière activité, pour remonter les élèves actifs en tête. */
  lastActivity: string;
}

/**
 * `evaluation` est le mode diagnostique — l'énumération serveur le dit
 * (« Test diagnostique — الاختبار التشخيصي »), et c'est ce que compose l'import.
 * Le nom vient de l'historique, pas du métier.
 */
function isDiagnostic(session: ProfessorStudentSessionDto): boolean {
  return (session.testMode ?? '').toLowerCase() === 'evaluation';
}

/** La pagination porte sur les élèves : c’est l’unité du suivi. */
const STUDENTS_PER_PAGE = 10;

/** Les trois regards proposés sur un élève, une fois son pli ouvert. */
type StudentPanelTab = 'sessions' | 'progress' | 'subjects';

/** Un point de la courbe, déjà placé dans le repère du tracé. */
interface ChartPoint {
  x: number;
  y: number;
  score: number;
  when: string;
  title: string;
  /** Le diagnostic sert de départ : c'est de lui qu'on mesure le chemin fait. */
  diagnostic: boolean;
}

/** Repère du tracé, en unités du `viewBox`. */
const CHART = { width: 320, height: 120, padX: 22, padY: 16 };

/** Le pourcentage d'une copie, quel que soit le champ qui le porte. */
function scoreOf(session: ProfessorStudentSessionDto): number | null {
  const raw = session.percentage ?? session.percentageScore ?? null;
  return raw === null || !Number.isFinite(Number(raw)) ? null : Number(raw);
}

/**
 * Vrai pour une copie <b>rendue</b>, donc porteuse d'une note.
 *
 * <p>Le pourcentage seul ne suffit pas à en juger : une tentative en cours vaut
 * <c>0</c>, et non <c>null</c>. Sans le statut, elle se lit comme un zéro
 * obtenu — un élève qui a ouvert son diagnostic sans le finir se voyait
 * annoncer « diagnostic 0 % ».</p>
 */
function isScored(session: ProfessorStudentSessionDto): boolean {
  return (session.status ?? '').toLowerCase() === 'completed' && scoreOf(session) !== null;
}

@Component({
  selector: 'app-student-sessions-tab',
  templateUrl: './student-sessions-tab.component.html',
  styleUrls: ['./student-sessions-tab.component.css'],
  standalone: true,
  imports: [CustomTabComponent, CommonModule, FormsModule, LoaderComponent, TranslateModule, CanDirective],
  encapsulation: ViewEncapsulation.None,
})
export class StudentSessionsTabComponent implements OnInit, OnDestroy {
  /** When set, filters sessions for a specific student */
  @Input() studentId: string | null = null;

  /**
   * Étendue des copies affichées.
   *
   * <p><c>own</c> — celles des matières que le professeur connecté enseigne,
   * la seule vue qui existait. <c>all</c> — celles de tout l'établissement,
   * pour l'administration.</p>
   *
   * <p>C'est le même écran parce que c'est la même table : le distinguer
   * garantirait qu'un jour l'une des deux dirait autre chose que l'autre.</p>
   */
  @Input() scope: 'own' | 'all' = 'own';

  /**
   * Racine des routes de détail, <c>/session/{id}</c> en étant le suffixe.
   *
   * <p>Elle était en dur sur l'espace professeur : un administrateur cliquant
   * une ligne atterrissait dans une coquille que son rôle n'ouvre pas.</p>
   */
  @Input() sessionRouteBase = '/teacher/my-students';

  // ── Facettes, réservées à l'étendue « tout l'établissement » ────────────
  //
  // Le professeur ne voit que ses propres matières : trois listes déroulantes
  // qui n'auraient qu'une valeur chacune n'ajouteraient rien à son écran. Le
  // filtrage est local — la période active tient dans une requête, et c'est
  // déjà le choix assumé par l'écran d'administration des tests.
  learningPathId: number | null = null;
  subjectId: number | null = null;
  professorId: number | null = null;
  /** Recherche d'un élève par son nom ou son adresse — avec les facettes. */
  studentSearch = '';

  /** Colonnes pour la table partagée (via app-custom-tab). */
  columns: any[] = [];

  data: ProfessorStudentSessionDto[] = [];
  isLoading = false;
  hasError = false;
  errorMessage = '';
  enableRTL = true;
  currentLocale = 'ar';

  pageSettings!: object;
  toolbar!: string[];

  translatedHeaders = {
    student: '',
    test: '',
    subject: '',
    learningPath: '',
    professor: '',
    status: '',
    score: '',
    passed: '',
    startedAt: '',
    completedAt: '',
    antifraud: '',
    actions: '',
  };

  private destroy$ = new RxSubject<void>();
  private professorService = inject(ProfessorService);
  private adminTestService = inject(AdminTestService);
  private translate = inject(TranslateService);
  private languageService = inject(LanguageService);
  private swal = inject(SwalService);
  private router = inject(Router);
  private studentService = inject(StudentService);
  private syncfusionL10n = inject(SyncfusionL10nService);

  async ngOnInit(): Promise<void> {
    const lang = this.languageService.getCurrentLang();
    this.setGridLanguage(lang);
    await this.loadTranslations();

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (e: LangChangeEvent) => {
        this.setGridLanguage(e.lang);
        await this.loadTranslations();
      });

    this.initGridSettings();
    this.loadSessions();
  }

  private setGridLanguage(lang: string): void {
    this.currentLocale = lang;
    this.enableRTL = lang === 'ar';
  }

  private initGridSettings(): void {
    this.pageSettings = { pageCount: 5, pageSize: 10, pageSizes: [10, 20, 50] };
    this.toolbar = ['Search'];
  }

  private async loadTranslations(): Promise<void> {
    try {
      const t = await firstValueFrom(
        this.translate.get([
          'FOLLOWING.COL_STUDENT',
          'FOLLOWING.COL_TEST',
          'FOLLOWING.COL_SUBJECT',
          'FOLLOWING.COL_LEARNING_PATH',
          'FOLLOWING.COL_PROFESSOR',
          'FOLLOWING.COL_STATUS',
          'FOLLOWING.COL_SCORE',
          'FOLLOWING.COL_PASSED',
          'FOLLOWING.COL_STARTED',
          'FOLLOWING.COL_COMPLETED',
          'FOLLOWING.COL_ANTIFRAUD',
          'DATA_TABLE.ACTIONS',
        ]),
      );
      this.translatedHeaders = {
        student: t['FOLLOWING.COL_STUDENT'],
        test: t['FOLLOWING.COL_TEST'],
        subject: t['FOLLOWING.COL_SUBJECT'],
        learningPath: t['FOLLOWING.COL_LEARNING_PATH'],
        professor: t['FOLLOWING.COL_PROFESSOR'],
        status: t['FOLLOWING.COL_STATUS'],
        score: t['FOLLOWING.COL_SCORE'],
        passed: t['FOLLOWING.COL_PASSED'],
        startedAt: t['FOLLOWING.COL_STARTED'],
        completedAt: t['FOLLOWING.COL_COMPLETED'],
        antifraud: t['FOLLOWING.COL_ANTIFRAUD'],
        actions: t['DATA_TABLE.ACTIONS'],
      };
      this.buildColumns();
    } catch {
      this.translatedHeaders = {
        student: 'Étudiant', test: 'Test', subject: 'Matière',
        learningPath: 'Parcours', professor: 'Professeur',
        status: 'Statut', score: 'Score', passed: 'Résultat',
        startedAt: 'Démarré', completedAt: 'Terminé',
        antifraud: 'Antifraud', actions: 'Actions',
      };
      this.buildColumns();
    }
  }

  /** Colonnes de la table partagée ; toutes les cellules riches passent par
   *  `cellTemplates` (badges, score, dates, antifraude). */
  private buildColumns(): void {
    const h = this.translatedHeaders;

    // Parcours et professeur n'apparaissent que pour l'administration : chez le
    // professeur, la colonne « professeur » répéterait son propre nom à chaque
    // ligne, et le parcours n'a pas de valeur discriminante sur ses matières.
    const transversal = this.showFilters
      ? [
          { key: 'learningPath', field: 'learningPathTitle', headerText: h.learningPath },
          { key: 'professor', field: 'professorName', headerText: h.professor },
        ]
      : [];

    this.columns = [
      { key: 'student', field: 'studentFullName', headerText: h.student },
      { key: 'test', field: 'testTitle', headerText: h.test },
      { key: 'subject', field: 'subjectName', headerText: h.subject },
      ...transversal,
      { key: 'status', field: 'status', headerText: h.status, textAlign: 'Center' },
      { key: 'score', field: 'percentage', headerText: h.score, textAlign: 'Center' },
      { key: 'passed', field: 'passed', headerText: h.passed, textAlign: 'Center' },
      { key: 'startedAt', field: 'startedAt', headerText: h.startedAt },
      { key: 'antifraud', headerText: h.antifraud, textAlign: 'Center' },
      { key: 'actions', headerText: h.actions, template: 'actionTemplate', textAlign: 'Center' },
    ];
  }

  /** Vrai quand l'écran couvre l'établissement : les facettes n'ont de sens que là. */
  get showFilters(): boolean {
    return this.scope === 'all' && !this.studentId;
  }

  /** Parcours présents dans les copies chargées. */
  get learningPathOptions(): { id: number; label: string }[] {
    return this.optionsFrom((s) =>
      s.learningPathId == null
        ? null
        : { id: s.learningPathId, label: s.learningPathTitle || `#${s.learningPathId}` },
    );
  }

  /**
   * Matières, restreintes au parcours choisi.
   *
   * <p>Sans parcours choisi, la liste les couvre tous et un même nom de matière
   * peut y revenir une fois par parcours ; le parcours est alors accolé pour
   * les distinguer. Une fois un parcours choisi, plus aucune collision n'est
   * possible et les libellés redeviennent nus d'eux-mêmes.</p>
   */
  get subjectOptions(): { id: number; label: string }[] {
    const scoped = this.learningPathId == null
      ? this.data
      : this.data.filter((s) => s.learningPathId === this.learningPathId);

    const byId = new Map<number, { name: string; path: string }>();
    for (const s of scoped) {
      if (s.subjectId == null || byId.has(s.subjectId)) continue;
      byId.set(s.subjectId, { name: s.subjectName || `#${s.subjectId}`, path: s.learningPathTitle });
    }

    const entries = [...byId.entries()];
    const seen = new Map<string, number>();
    for (const [, v] of entries) seen.set(v.name, (seen.get(v.name) ?? 0) + 1);

    return entries
      .map(([id, v]) => ({
        id,
        label: (seen.get(v.name) ?? 0) > 1 && v.path ? `${v.name} — ${v.path}` : v.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /** Auteurs des tests passés — c'est ce sens-là que « professeur » recouvre ici. */
  get professorOptions(): { id: number; label: string }[] {
    return this.optionsFrom((s) =>
      s.professorId == null
        ? null
        : { id: s.professorId, label: s.professorName?.trim() || `#${s.professorId}` },
    );
  }

  private optionsFrom(
    pick: (s: ProfessorStudentSessionDto) => { id: number; label: string } | null,
  ): { id: number; label: string }[] {
    const byId = new Map<number, string>();
    for (const session of this.data) {
      const option = pick(session);
      if (option && !byId.has(option.id)) byId.set(option.id, option.label);
    }
    return [...byId.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /** Les copies réellement affichées, une fois les facettes appliquées. */
  get visibleData(): ProfessorStudentSessionDto[] {
    if (!this.showFilters) return this.data;

    return this.data.filter((s) => {
      if (this.learningPathId != null && s.learningPathId !== this.learningPathId) return false;
      if (this.subjectId != null && s.subjectId !== this.subjectId) return false;
      if (this.professorId != null && s.professorId !== this.professorId) return false;
      return matchesSearch(this.studentSearch, s.studentFullName, s.studentEmail);
    });
  }

  /* ── Suivi par élève ─────────────────────────────────────────── */

  /**
   * Les copies regroupées par élève, l'élève le plus récemment actif en tête.
   *
   * <p>La table listait une ligne par passage. Le rapport de diagnostic est
   * pourtant une propriété de l'<b>élève</b>, pas de chacune de ses tentatives :
   * répété sur chaque ligne, il devenait illisible dès qu'un élève en avait
   * passé plusieurs, et laissait croire qu'il en existe un par copie.</p>
   */
  get studentGroups(): StudentGroup[] {
    const byStudent = new Map<string, StudentGroup>();

    for (const session of this.visibleData) {
      let group = byStudent.get(session.studentId);

      if (!group) {
        group = {
          studentId: session.studentId,
          fullName: session.studentFullName || session.studentEmail || session.studentId,
          email: session.studentEmail,
          learningPathTitle: session.learningPathTitle,
          sessions: [],
          diagnosticCount: 0,
          lastActivity: '',
        };
        byStudent.set(session.studentId, group);
      }

      group.sessions.push(session);
      if (isDiagnostic(session)) group.diagnosticCount++;

      const when = session.completedAt || session.startedAt || '';
      if (when > group.lastActivity) group.lastActivity = when;
    }

    const groups = [...byStudent.values()];
    for (const g of groups) {
      g.sessions.sort((a, b) =>
        (b.completedAt || b.startedAt || '').localeCompare(a.completedAt || a.startedAt || ''),
      );
    }

    return groups.sort((a, b) => b.lastActivity.localeCompare(a.lastActivity));
  }

  /** Élèves de la page courante. La pagination porte sur eux, pas sur les copies. */
  get pagedStudentGroups(): StudentGroup[] {
    const start = this.currentStudentPage * STUDENTS_PER_PAGE;
    return this.studentGroups.slice(start, start + STUDENTS_PER_PAGE);
  }

  /**
   * Page réellement affichée : bornée au nombre de pages du moment.
   *
   * <p>Un filtre raccourcit la liste. Lire `studentPage` tel quel laissait
   * l'écran en page 3 d'une liste qui n'en comptait plus qu'une : aucun élève
   * affiché, et plus de pagination pour en sortir — elle ne se rend qu'à partir
   * de deux pages.</p>
   */
  get currentStudentPage(): number {
    return Math.min(this.studentPage, this.studentPageCount - 1);
  }

  get studentPageCount(): number {
    return Math.max(1, Math.ceil(this.studentGroups.length / STUDENTS_PER_PAGE));
  }

  studentPage = 0;

  /** Le pli est ouvert par élève ; le premier de la page l'est d'office. */
  private readonly expanded = new Set<string>();

  isExpanded(group: StudentGroup): boolean {
    return this.expanded.has(group.studentId);
  }

  toggleStudent(group: StudentGroup): void {
    if (!this.expanded.delete(group.studentId)) this.expanded.add(group.studentId);
  }

  goToStudentPage(page: number): void {
    this.studentPage = Math.min(Math.max(0, page), this.studentPageCount - 1);
  }

  /**
   * Ouvre le rapport de diagnostic de l'élève.
   *
   * <p>Le rapport porte sur l'élève et non sur une copie : il croise toutes ses
   * réponses diagnostiques pour situer ce qu'il maîtrise.</p>
   *
   * <p><code>subjectId</code> préselectionne l'onglet d'une matière. C'est le
   * cas quand on part d'une copie : celle-ci porte une matière, et c'est
   * d'elle qu'on veut parler. Depuis la ligne de l'élève, aucune matière n'est
   * désignée et le rapport s'ouvre sur l'ensemble.</p>
   */
  openDiagnosticReport(studentId: string, subjectId?: number | null): void {
    this.router.navigate(['/diagnostic-report/students', studentId], {
      queryParams: subjectId ? { subject: subjectId } : {},
    });
  }

  /* ── Le panneau de l'élève : copies, progression, matières ───── */

  private readonly tabs = new Map<string, StudentPanelTab>();

  /** Les copies restent le premier regard : c'est ce qu'on vient voir. */
  tabOf(group: StudentGroup): StudentPanelTab {
    return this.tabs.get(group.studentId) ?? 'sessions';
  }

  selectTab(group: StudentGroup, tab: StudentPanelTab): void {
    this.tabs.set(group.studentId, tab);
    if (tab === 'subjects') this.loadSubjectProgression(group);
  }

  /**
   * Les copies notées d'un élève, placées dans le repère du tracé.
   *
   * <p>Calculé sur place : le score, la date et le mode sont déjà dans les
   * copies chargées. Un appel de plus ne dirait rien que l'écran ne sache.</p>
   *
   * <p>L'axe vertical va de 0 à 100 quoi qu'il arrive. Le cadrer sur les valeurs
   * observées ferait paraître spectaculaire un écart de deux points.</p>
   */
  chartOf(group: StudentGroup): ChartPoint[] {
    const notes = group.sessions
      // Seules les copies rendues portent une note. Une tentative en cours a un
      // pourcentage à `0`, pas à `null` : elle passait donc le filtre et se
      // traçait comme un zéro. Un élève ayant ouvert son diagnostic sans le
      // finir se voyait annoncer « diagnostic 0 % », et sa moyenne avec.
      .filter((s) => isScored(s) && (s.completedAt || s.startedAt))
      .map((s) => ({
        when: (s.completedAt || s.startedAt) as string,
        score: scoreOf(s) as number,
        title: s.testTitle || '',
        diagnostic: isDiagnostic(s),
      }))
      .sort((a, b) => a.when.localeCompare(b.when));

    if (notes.length === 0) return [];

    const span = CHART.width - CHART.padX * 2;
    const usable = CHART.height - CHART.padY * 2;

    return notes.map((n, i) => ({
      ...n,
      x: CHART.padX + (notes.length === 1 ? span / 2 : (span * i) / (notes.length - 1)),
      y: CHART.padY + usable * (1 - Math.min(100, Math.max(0, n.score)) / 100),
    }));
  }

  /** Le tracé, sous la forme attendue par `<polyline points="…">`. */
  polylineOf(group: StudentGroup): string {
    return this.chartOf(group)
      .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' ');
  }

  /** Note du diagnostic — le point de départ dont on mesure l'écart. */
  diagnosticScoreOf(group: StudentGroup): number | null {
    const first = this.chartOf(group).find((p) => p.diagnostic);
    return first ? first.score : null;
  }

  /** Écart entre la dernière note et celle du diagnostic. */
  progressOf(group: StudentGroup): number | null {
    const points = this.chartOf(group);
    const start = this.diagnosticScoreOf(group);
    if (start === null || points.length === 0) return null;

    return Math.round(points[points.length - 1].score - start);
  }

  averageOf(group: StudentGroup): number | null {
    const points = this.chartOf(group);
    if (points.length === 0) return null;

    return Math.round(points.reduce((sum, p) => sum + p.score, 0) / points.length);
  }

  /* ── Progression par matière, calculée par le serveur ───────── */

  private readonly subjectProgression = new Map<string, StudentSubjectProgression[]>();
  private readonly subjectLoading = new Set<string>();

  progressionOf(group: StudentGroup): StudentSubjectProgression[] {
    return this.subjectProgression.get(group.studentId) ?? [];
  }

  isProgressionLoading(group: StudentGroup): boolean {
    return this.subjectLoading.has(group.studentId);
  }

  /**
   * Charge la progression par matière, une fois par élève.
   *
   * <p>Le serveur la calcule déjà — taux d'avancement et moyenne par matière.
   * La refaire ici à partir des copies donnerait un second chiffre, proche mais
   * différent, et personne ne saurait lequel croire.</p>
   */
  private loadSubjectProgression(group: StudentGroup): void {
    if (this.subjectProgression.has(group.studentId)) return;
    if (this.subjectLoading.has(group.studentId)) return;

    const id = Number(group.studentId);
    if (!Number.isFinite(id)) return;

    this.subjectLoading.add(group.studentId);
    this.studentService.getSubjectsProgression(id).subscribe({
      next: (res) => {
        this.subjectProgression.set(group.studentId, res?.data ?? []);
        this.subjectLoading.delete(group.studentId);
      },
      error: () => {
        this.subjectProgression.set(group.studentId, []);
        this.subjectLoading.delete(group.studentId);
      },
    });
  }

  /** Changer de parcours invalide la matière : elle pourrait ne plus s'y trouver. */
  onLearningPathChange(): void {
    this.subjectId = null;
    this.studentPage = 0;
  }

  /** Une facette qui change invalide la position : on repart de la première page. */
  onFacetChange(): void {
    this.studentPage = 0;
  }

  resetFilters(): void {
    this.learningPathId = null;
    this.subjectId = null;
    this.professorId = null;
    this.studentSearch = '';
    this.studentPage = 0;
  }

  get hasActiveFilter(): boolean {
    return this.learningPathId != null
      || this.subjectId != null
      || this.professorId != null
      || this.studentSearch.trim().length > 0;
  }

  loadSessions(): void {
    this.isLoading = true;
    this.hasError = false;

    // Deux routes pour la même table : celle du professeur ne rend que les
    // matières qu'il enseigne, celle de l'administration ne filtre rien.
    const obs$ = this.scope === 'all'
      ? (this.studentId
          ? this.adminTestService.studentActiveSessions(this.studentId)
          : this.adminTestService.activeSessions())
      : (this.studentId
          ? this.professorService.getActivePeriodSessionsByStudent(this.studentId)
          : this.professorService.getActivePeriodSessions());

    obs$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.data = res.data ?? [];
        this.isLoading = false;
      },
      error: (err) => {
        console.error('❌ Error loading sessions:', err);
        this.hasError = true;
        this.errorMessage = err?.message ?? 'Error loading sessions';
        this.isLoading = false;
        this.swal.error(this.translate.instant('SWAL.FETCH_USER_ERROR'));
      },
    });
  }

  viewSession(session: ProfessorStudentSessionDto): void {
    // Explicit field mapping to strip any Syncfusion-injected non-serializable
    // properties (e.g. editTemplateFn) before passing to history.pushState.
    const cleanSession: ProfessorStudentSessionDto = {
      id: session.id,
      studentId: session.studentId,
      studentFullName: session.studentFullName,
      studentEmail: session.studentEmail,
      testId: session.testId,
      testTitle: session.testTitle,
      testMode: session.testMode,
      subjectId: session.subjectId,
      subjectName: session.subjectName,
      learningPathId: session.learningPathId,
      learningPathTitle: session.learningPathTitle,
      professorId: session.professorId,
      professorName: session.professorName,
      status: session.status,
      startedAt: session.startedAt,
      completedAt: session.completedAt,
      timeSpentSeconds: session.timeSpentSeconds,
      attemptNumber: session.attemptNumber,
      score: session.score,
      percentage: session.percentage,
      percentageScore: session.percentageScore,
      passed: session.passed,
      correctAnswers: session.correctAnswers,
      wrongAnswers: session.wrongAnswers,
      unansweredQuestions: session.unansweredQuestions,
      totalQuestions: session.totalQuestions,
      totalPoints: session.totalPoints,
      earnedPoints: session.earnedPoints,
      tabSwitchCount: session.tabSwitchCount,
      focusLossCount: session.focusLossCount,
      fullscreenExitCount: session.fullscreenExitCount,
    };
    this.router.navigate([`${this.sessionRouteBase}/session`, cleanSession.id], {
      state: { session: cleanSession },
    });
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'completed': return 'badge bg-success';
      case 'in_progress': return 'badge bg-warning text-dark';
      case 'expired': return 'badge bg-secondary';
      default: return 'badge bg-light text-dark';
    }
  }

  getPassedClass(passed: boolean | null): string {
    if (passed === null) return 'badge bg-secondary';
    return passed ? 'badge bg-success' : 'badge bg-danger';
  }

  hasAntifraudFlag(session: ProfessorStudentSessionDto): boolean {
    return session.tabSwitchCount > 2 || session.focusLossCount > 3 || session.fullscreenExitCount > 1;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
